
import React from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Building, Building2, Layers } from 'lucide-react';

interface Zone {
  id: string;
  name: string;
  temperature: number;
  humidity: number;
  status: 'active' | 'inactive' | 'maintenance';
}

interface Floor {
  id: number;
  name: string;
  zones: Zone[];
}

interface HvacFloorMapProps {
  selectedFloor: number;
  onFloorChange: (floor: number) => void;
  selectedZone: string | null;
  onZoneSelect: (zoneId: string) => void;
}

const floors: Floor[] = [
  {
    id: 1,
    name: 'Ground Floor',
    zones: [
      { id: 'gf-zone1', name: 'Reception', temperature: 22.5, humidity: 45, status: 'active' },
      { id: 'gf-zone2', name: 'Lobby', temperature: 23.1, humidity: 48, status: 'active' },
      { id: 'gf-zone3', name: 'Meeting Room A', temperature: 21.8, humidity: 42, status: 'active' },
      { id: 'gf-zone4', name: 'Cafeteria', temperature: 24.2, humidity: 50, status: 'active' },
    ]
  },
  {
    id: 2,
    name: 'First Floor',
    zones: [
      { id: 'ff-zone1', name: 'Open Office', temperature: 22.8, humidity: 46, status: 'active' },
      { id: 'ff-zone2', name: 'Meeting Room B', temperature: 21.5, humidity: 44, status: 'inactive' },
      { id: 'ff-zone3', name: 'Executive Office', temperature: 23.0, humidity: 47, status: 'active' },
      { id: 'ff-zone4', name: 'IT Department', temperature: 20.5, humidity: 40, status: 'maintenance' },
    ]
  },
  {
    id: 3,
    name: 'Second Floor',
    zones: [
      { id: 'sf-zone1', name: 'Research Lab', temperature: 20.0, humidity: 38, status: 'active' },
      { id: 'sf-zone2', name: 'Development', temperature: 21.3, humidity: 42, status: 'active' },
      { id: 'sf-zone3', name: 'Conference Room', temperature: 22.5, humidity: 45, status: 'active' },
      { id: 'sf-zone4', name: 'Break Room', temperature: 23.5, humidity: 48, status: 'inactive' },
    ]
  }
];

const HvacFloorMap: React.FC<HvacFloorMapProps> = ({ 
  selectedFloor, 
  onFloorChange,
  selectedZone,
  onZoneSelect
}) => {
  const handleFloorChange = (value: string) => {
    onFloorChange(parseInt(value));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-400';
      case 'maintenance':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getTemperatureColor = (temp: number) => {
    if (temp >= 24) return 'text-red-600 dark:text-red-400';
    if (temp >= 22) return 'text-orange-600 dark:text-orange-400';
    return 'text-blue-600 dark:text-blue-400';
  };

  return (
    <div className="p-4 h-full">
      <h2 className="text-lg font-semibold mb-4 flex items-center">
        <Building2 className="mr-2 h-5 w-5" />
        Floor-wise Temperature & Humidity Readings
      </h2>
      
      <Tabs value={selectedFloor.toString()} onValueChange={handleFloorChange} className="w-full">
        <TabsList className="mb-4">
          {floors.map(floor => (
            <TabsTrigger key={floor.id} value={floor.id.toString()} className="flex items-center">
              <Layers className="mr-1 h-4 w-4" />
              {floor.name}
            </TabsTrigger>
          ))}
        </TabsList>

        {floors.map(floor => (
          <TabsContent key={floor.id} value={floor.id.toString()} className="mt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {floor.zones.map(zone => (
                <div 
                  key={zone.id}
                  className={`p-4 rounded-lg border ${selectedZone === zone.id ? 'border-blue-500 dark:border-blue-400' : 'border-gray-200 dark:border-gray-700'} 
                    cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition-colors`}
                  onClick={() => onZoneSelect(zone.id)}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium">{zone.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(zone.status)}`}>
                      {zone.status.charAt(0).toUpperCase() + zone.status.slice(1)}
                    </span>
                  </div>

                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center">
                      <span className={`text-lg font-bold ${getTemperatureColor(zone.temperature)}`}>
                        {zone.temperature}°C
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      Humidity: {zone.humidity}%
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default HvacFloorMap;
